/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './OverAllUpTime.css';
import { IconWithTooltip } from '../../globalUtils/globalIcons';
import type { AxisLabelsFormatterContextObject } from 'highcharts';
import { useAppDispatch, useAppSelector } from '../../rmsReduxStore/rmsReduxHooks';
import { getCurrentFiltersValues, getCurrentScreenAppliedFiltersCount, getDashboardReducer, getFilterSelectedValue, getReferenceDataReducer, setFilterData } from '../../rmsReduxStore/reduxExports';
import { renderHeaderActionButtons, SelectDropDown, TableSubHeader, useEffectOnce } from '../../globalUtils/globalExports';
import { RectangularSkeleton } from '../../globalUtils/SkeletonDesign/SkeletonDesign';
import CustomFilterLayout, { CustomFilterProp } from './CustomFilterLayout';

interface OverallUptimeProp {
  handleFilter: Function,
  getFilterValue: Function
}

const OverAllUpTime: React.FC<OverallUptimeProp> = (props) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { handleFilter, getFilterValue } = props;
  const { intervalRangeReferenceData,chargerVisibleIdReferenceData,chargingStationReferenceData } = useAppSelector(getReferenceDataReducer);
  // const [filterValue, setFilterValue] = useState<IGeneralReferenceData>()
  const dispatch = useAppDispatch();
  const navigationUpperLimit = 31
  const [visibleMin, setVisibleMin] = useState(0);
  const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
  const [ chartHeight, setChartHeight ] = useState(400);
  const { newDashboardOverallUptimeInfo } = useAppSelector(getDashboardReducer);
  const [filterAnchorEl,setFilterAnchorEl] = useState<HTMLElement | null>(null);

  // useEffectOnce(() => {
  //   setFilterValue(getFilterValue('uptimeIntervalRange'))
  // })

  useEffect(() => {
    const targetElement = document.getElementsByClassName('overall-uptime-body');

    if (!targetElement?.[0]) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height } = entry.contentRect;
        setChartHeight(Math.min(height, 550));
      }
    });

    resizeObserver.observe(targetElement[0]);

    return () => {
      resizeObserver.disconnect(); // Clean up
    };
  }, []);


  const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const rawValue = this.value;
    const label = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
    const [day, month] = label.split(' ');
  
    const chartWidth = this.axis.chart.chartWidth;
    const numCategories = this.axis.categories?.length || 1;
    const approxCategoryWidth = chartWidth / numCategories;
  
    const shouldWrap = approxCategoryWidth > 0; // adjust threshold if needed
  
    return shouldWrap
      ? `<div style="text-align: center;">${day}<br>${month}</div>`
      : `<div style="text-align: center;">${day}<br><span style="opacity: 0.6; font-size: 10px;">${month}</span></div>`;
  }; 
  const getFormattedData = useCallback(() => {
    const data = newDashboardOverallUptimeInfo || [];
  
    const slicedData = data.slice(visibleMin, visibleMax + 1);
  
    const xAxis = slicedData.map(item => item?.date);
    const unifiedSeries = slicedData.map(item => item?.data ?? 0);

    const trendline = unifiedSeries.map((_, idx) => {
      const idx1 = idx - 2 >= 0 ? idx - 2 : 0;
      const idx2 = idx - 1 >= 0 ? idx - 1 : 0;
      const idx3 = idx;
      const idx4 = idx + 1 < unifiedSeries.length ? idx + 1 : unifiedSeries.length - 1;
      const idx5 = idx + 2 < unifiedSeries.length ? idx + 2 : unifiedSeries.length - 1;
    
      const sum = unifiedSeries[idx1] + unifiedSeries[idx2] + unifiedSeries[idx3] + unifiedSeries[idx4] + unifiedSeries[idx5];
      return +(sum / 5).toFixed(2);
    });
    
  
    return {
      xAxis,
      series: {
        value: unifiedSeries,
        trend: trendline,
      }
    };
  }, [newDashboardOverallUptimeInfo, visibleMin, visibleMax]);


  let labelStep = 1;  //draft
  const { xAxis } = getFormattedData();
  const totalBars = xAxis.length;
  if (totalBars > 20) labelStep = 4;
  else if (totalBars > 10) labelStep = 2;
  


  const { series } = getFormattedData();
  const trendline = series.trend;
  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: chartHeight,
      spacing: [15, 10, 10, 10],
      events: {
        render: function () {
          const chart = this as Highcharts.Chart & any;
  
          // Remove existing custom arrows before adding new ones
          const existingArrows = chart.container.getElementsByClassName('custom-arrow');
          while (existingArrows.length > 0) {
            existingArrows[0].remove();
          }
  
          // Left arrow (shifted more inside by changing left position)
          if (visibleMin > 0) {
            chart.renderer.image(
              '/images/left_arrow_highchart.svg',
              chart.plotLeft, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow left')
            .on('click', () => handleNavigation('left'))
            .add();
          }
  
          // Right arrow (shifted more inside by changing left position)
          if (visibleMax < dataLength - 1) {
            chart.renderer.image(
              '/images/right_arrow_highchart.svg',
              chart.plotLeft + chart.plotWidth - 15, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow right')
            .on('click', () => handleNavigation('right'))
            .add();
          }
        }
      },
    },
    title: { text: undefined },
    subtitle: { text: undefined },
    xAxis: {
      categories: xAxis, //todo
      lineColor: '#005EFF',
      tickColor: '#005EFF',
      gridLineWidth: (newDashboardOverallUptimeInfo?.length ?? 0) > 10 ? 0 : 1,
      gridLineDashStyle: 'ShortDash', 
      plotBands: getFormattedData().xAxis.map((_, index) => {
        const barWidthFraction = 0.25;
        return {
          from: index - barWidthFraction,
          to: index + barWidthFraction,
          color: '#f5f5f5'
        };
      }),          
      labels: {
        rotation: 0,
        step: labelStep,
        // useHTML: true,
        formatter: xAxisLabelFormatter,
      }

    },
    yAxis: {
      title: { text: 'Percentage' },
      gridLineDashStyle: 'ShortDash',
      // tickInterval: 20
    },
    tooltip: {
      shared: true,
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      enabled: true,
      useHTML: true,
      labelFormatter: function () {
        const isVisible = this.visible !== false;
        const textStyle = isVisible ? '' : 'color: #999; text-decoration: line-through;';
        const boxColor = isVisible ? this.color : '#ccc';
    
        return `
          <span style="display: inline-flex; align-items: center; gap: 6px; ${textStyle}">
            <span style="width: 10px; height: 10px; background-color: ${boxColor}; display: inline-block; border-radius: 50%;"></span>
            <span>${this.name}</span>
          </span>
        `;
      },
      itemStyle: {
        fontSize: '12px',
        cursor: 'default',
      },
      symbolWidth: 0,
      symbolHeight: 0,
      symbolPadding: 0,
      symbolRadius: 0
    },
    plotOptions: {
      column: {
        borderRadius: 8,
        pointPadding: 0.2,
        groupPadding: 0.1,
      },
      series: {
        marker: {
          enabled: true,
        },
      },
    },
    series: [
      {
        name: 'Uptime',
        type: 'column',
        data: series.value,
        color: 'var(--secondary-blue-color)',
      },
      // {
      //   name: 'Trendline',
      //   type: 'line',
      //   data: trendline,
      //   color: '#D21616',
      //   lineWidth: 2,
      //   marker: {
      //     enabled: false,
      //   },
      // },
    ],
    credits: { enabled: false },
    exporting: { enabled: false },
  };
  getFormattedData();

  // const dataLength = newDashboardFailedTransactionList?.length || 0;
  const dataLength = 7

  const handleNavigation = (direction: 'left' | 'right') => {
    const step = navigationUpperLimit + 1;
    if (direction === 'left') {
        setVisibleMin(Math.max(visibleMin - step, 0));
        setVisibleMax(Math.max(visibleMax - step, step));
    } else {
        setVisibleMin(Math.min(visibleMin + step, dataLength - (step + 1)));
        setVisibleMax(Math.min(visibleMax + step, dataLength - 1));
    }
};


// const handleSelectChange = (val): void => {
//   setFilterValue(val);
//   handleEnergyTrendFilter(val, 'uptimeIntervalRange');
//   handleChartPageReset();
// };

const handleChartPageReset = () => {
  setVisibleMin(0);
  setVisibleMax(navigationUpperLimit);
}
// const handleEnergyTrendFilter = useCallback(async (val: any, filterKey: string): Promise<void> => {
//   const res = await setFilterData(val, filterKey, 'dashboardV2', 'SET', false)(dispatch)
//   if (res?.message === 'Action dispatched successfully') {
//       handleFilter(res?.filters);
//   }
// }, [setFilterData])

 const headerActionBtns = renderHeaderActionButtons({
        actionBtns:
            [
                {
                    id: 'add_filter_btn',
                    isAccessible: true,
                    headerActionBtnClick: (e): void => {
                        setFilterAnchorEl(e.currentTarget)
                    },
                    badgeCount: getCurrentScreenAppliedFiltersCount('overallUptime',true)
                },
            ],
  })

  const handleFilterDataChange = async (val, filterKey, isGlobal) => {
      const res = await setFilterData(val, filterKey, 'overallUptime', 'SET', isGlobal ? isGlobal : false,true)(dispatch)
      const dashboardV2Filters = getCurrentFiltersValues('dashboardV2')
      
      if (res?.message === 'Action dispatched successfully') {
        handleFilter(dashboardV2Filters,res?.filters)
        handleChartPageReset();
      }
  }

  const overAllUpTimeFilterProps: CustomFilterProp[] = useMemo(()=>{
    return [
      {
        filterLabel: 'Charging Stations',
        filterType: 'dropdown',
        filterId: 'chargingStationId',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('chargingStationId', 'overallUptime', false),
          selectOptions: chargingStationReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargingStationId', false)
          },
          selectDropDownId: 'overall-uptime-chargingStationId-type-filter-dropdown',
          isMultiSelect: true,
          renderToBody:true,
        }
      },
      {
        filterLabel: 'Chargers',
        filterId: 'chargerId',
        filterType: 'dropdown',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('chargerId', 'overallUptime', false),
          selectOptions: chargerVisibleIdReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargerId', false)
          },
          selectDropDownId: 'overall-uptime-charger-id-filter-dropdown',
          isMultiSelect: true,
          renderToBody: true
        }
      },
      {
        filterLabel: 'Date Range',
        filterId: 'uptimeIntervalRange',
        filterType: 'dropdown',
        filterDropdownProps: {
          selectValue: getFilterSelectedValue('uptimeIntervalRange', 'overallUptime', false),
          selectOptions: intervalRangeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'uptimeIntervalRange', false)
          },
          selectDropDownId: 'overall-uptime-date-range-filter-dropdown',
          isMultiSelect: false,
          renderToBody: true
        }
      },
    ]
  },[intervalRangeReferenceData,chargerVisibleIdReferenceData,chargingStationReferenceData,handleFilterDataChange,getFilterSelectedValue])

  return (
    <div className="widget-wrap overall-uptime-wrap">
      <div className="widget-header chart-header">
        <div className='widget-heading overall-uptime-header'>
          Overall Uptime of Chargers
          <IconWithTooltip iconClassName="info-icon" toolTipText={'Uptime, Online and Offline chargers, etc.'} containerClassName={'new-dashboard-widget-header-tooltip'} />
        </div>
        <div className="chart-filters">
            {/* <SelectDropDown
            selectValue={filterValue}
            selectOptions={intervalRangeReferenceData ?? []}
            handleSelectOptionChange={handleSelectChange}
            selectDropDownId='alarm-failed-transaction-dropdown'
            dropDownClassName='alarm-failed-transaction-dropdown'
            isMultiSelect={false}
            placeholder={'Interval'}
          />   */}
          <TableSubHeader headerActionBtns={headerActionBtns}/>
          <CustomFilterLayout filters={overAllUpTimeFilterProps} requiredReferenceDataCallbacks={['chargerVisibleId','chargingStationId']} filterAnchorEl={filterAnchorEl} setFilterAnchorEl={setFilterAnchorEl} excludeGlobalFilters={true} />
        </div>
      </div>
      <div className='widget-body overall-uptime-body'>
        { !newDashboardOverallUptimeInfo ? 
          <div style={{ height: `${chartHeight}px` }}>
            <RectangularSkeleton skeletonContainerClassName='' />
          </div> 
          :
          <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef}/>
        }
      </div>
    </div>
  );
};

export default OverAllUpTime;